import {
  Box, Tabs, Tab, Stack, Typography, alpha, useTheme,
  Skeleton, Badge, Button, CircularProgress,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlerts } from '../../hooks/useAlerts';
import { useAuth, useNotification } from '../../hooks';
import { PageContainer } from '../../components';
import AlertInstanceList from './AlertInstanceList';
import AlertConfigPanel from './AlertConfigPanel';
import { USER_ROLES } from '../../constants/permissions';

/* ───── Inline severity stat pill ───── */
const SeverityPill = ({ icon, count, color, label }) => {
  const theme = useTheme();
  if (count === 0) return null;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.4, py: 0.5, borderRadius: 99,
      bgcolor: alpha(theme.palette[color].main, 0.08),
      border: `1px solid ${alpha(theme.palette[color].main, 0.15)}`,
    }}>
      {icon}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: `${color}.main`, lineHeight: 1 }}>
        {count}
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary', lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
};

const AlertsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const { instances, dismissedInstances, loading, dismissAlert, undismissAlert, fetchInstances } = useAlerts();
  const isRoot = user?.role === USER_ROLES.ROOT;

  // Manuel tetik: gece 3'te calisan alert cron'u simdi calistir.
  // Backend /alerts/evaluate endpoint'i API_KEY ile korunuyor;
  // AlertConfigPanel ile ayni pattern.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const apiKey = process.env.REACT_APP_API_KEY || 'AI_THHINKS_API_KEY';
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3006';
      const res = await fetch(`${backendUrl}/alerts/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchInstances();
      showNotification(t('alerts.refreshSuccess', 'Uyarılar güncellendi'), 'success');
    } catch (e) {
      showNotification(
        t('alerts.refreshError', 'Uyarılar güncellenemedi: {{msg}}', { msg: e?.message || 'bilinmeyen hata' }),
        'error',
      );
    } finally {
      setRefreshing(false);
    }
  };

  const critical = instances.filter(i => i.alert_type_config?.severity === 'critical').length;
  const warning = instances.filter(i => i.alert_type_config?.severity === 'warning').length;
  const info = instances.filter(i => i.alert_type_config?.severity === 'info').length;

  const getTabContent = () => {
    if (activeTab === 0) {
      return (
        <AlertInstanceList
          instances={instances}
          loading={loading}
          onDismiss={dismissAlert}
        />
      );
    }
    if (activeTab === 1) {
      return (
        <AlertInstanceList
          instances={dismissedInstances}
          loading={loading}
          onUndismiss={undismissAlert}
          isDismissedView
        />
      );
    }
    if (isRoot && activeTab === 2) {
      return <AlertConfigPanel instances={instances} />;
    }
    return null;
  };

  return (
    <PageContainer currentPage={t('alerts.title')}>
      <Box sx={{ py: 3, px: { xs: 1.5, sm: 2.5 } }}>

        {/* ───── Header ───── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.85)})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}>
              <NotificationsActiveIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: 18, sm: 20 }, lineHeight: 1.2 }}>
                {t('alerts.center')}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: 12 }}>
                {instances.length > 0
                  ? t('alerts.totalActive', { count: instances.length })
                  : t('alerts.noAlertsDesc')
                }
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
            {/* Severity pills */}
            {!loading && instances.length > 0 && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                <SeverityPill
                  icon={<ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                  count={critical} color="error" label={t('alerts.severity.critical')}
                />
                <SeverityPill
                  icon={<WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />}
                  count={warning} color="warning" label={t('alerts.severity.warning')}
                />
                <SeverityPill
                  icon={<InfoOutlinedIcon sx={{ fontSize: 14, color: 'info.main' }} />}
                  count={info} color="info" label={t('alerts.severity.info')}
                />
              </Stack>
            )}

            {loading && (
              <Stack direction="row" gap={1}>
                {[1, 2].map(i => <Skeleton key={i} variant="rounded" width={100} height={28} sx={{ borderRadius: 99 }} />)}
              </Stack>
            )}

            {/* Yenile butonu — gece calisan cron'u manuel tetikler ve
                sonrasinda instances'i tekrar cekilir. */}
            <Button
              size="small"
              variant="outlined"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('alerts.refresh', 'Yenile')}
            </Button>
          </Stack>
        </Stack>

        {/* ───── Main card ───── */}
        <Box sx={{
          borderRadius: 3, bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}, 0 4px 16px ${alpha(theme.palette.common.black, 0.03)}`,
          overflow: 'hidden',
        }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48, fontSize: 13, fontWeight: 600,
                  textTransform: 'none', px: 2, gap: 0.75, minWidth: 'auto',
                  color: 'text.secondary',
                  '&.Mui-selected': { color: 'text.primary' },
                },
                '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2.5px 2.5px 0 0' },
              }}
            >
              <Tab
                icon={
                  <Badge
                    badgeContent={instances.length}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: 9, fontWeight: 700, minWidth: 16, height: 16,
                        display: instances.length > 0 ? 'flex' : 'none',
                      },
                    }}
                  >
                    <NotificationsActiveIcon sx={{ fontSize: 17 }} />
                  </Badge>
                }
                iconPosition="start"
                label={t('alerts.myAlerts')}
              />
              <Tab
                icon={
                  <Badge
                    badgeContent={dismissedInstances.length}
                    color="default"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: 9, fontWeight: 700, minWidth: 16, height: 16,
                        bgcolor: 'action.selected', color: 'text.secondary',
                        display: dismissedInstances.length > 0 ? 'flex' : 'none',
                      },
                    }}
                  >
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
                  </Badge>
                }
                iconPosition="start"
                label={t('alerts.dismissedTab')}
              />
              {isRoot && (
                <Tab
                  icon={<TuneIcon sx={{ fontSize: 17 }} />}
                  iconPosition="start"
                  label={t('alerts.config_label')}
                />
              )}
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            {getTabContent()}
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};

export default AlertsPage;
